/**
 * Invoice detail header — mirrors `prototype/sales/invoice-detail.html` ▸
 * `.composer-head` verbatim. Page-scoped `.status-pill` is used instead
 * of `<StatusPill>` from `@flint/ui` because the prototype owns the
 * pill's chrome — see `invoice-detail.css`.
 *
 * Subtitle uses `<p class="sub">` with `.num` / `.sep` / `.due-soft` /
 * `.due-late` children so the CSS can colour the due-date emphasis.
 *
 * Action group includes a `.split` button cluster (`.lead` + `.chev`)
 * for "Send via WhatsApp ▾" / "Issue and send ▾" on non-drafts.
 */
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  Copy,
  Download,
  FileMinus,
  FileText,
  HandCoins,
  Pencil,
  Printer,
  Send,
  Trash2,
} from "lucide-react";
import {
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "@flint/ui";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";

/** Invoice lifecycle statuses surfaced by `invoices.get`. */
export type InvoiceStatus =
  | "draft"
  | "issued"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "void"
  | "reversed";

/** Inputs and action callbacks for the sticky invoice detail header. */
export interface InvoiceDetailHeaderProps {
  invoice: {
    id: string;
    docNumber: string | null;
    status: InvoiceStatus;
    issuedOn: string;
    dueOn: string;
    totalPaise: string;
  };
  customer: { name: string; phone: string | null };
  isFounder: boolean;
  onEdit: () => void;
  onSendWhatsApp: () => void;
  onPrint: () => void;
  onDownloadPdf: () => void;
  onIssue: (channel: "whatsapp" | "email" | "print-later" | "issue-only") => void;
  onDeleteDraft: () => void;
  onRecordReceipt: () => void;
  onClone: () => void;
  onRaiseCreditNote: () => void;
  /**
   * True when the invoice has already been fully credit-noted. The
   * domain refuses a second full-reversal CN; we mirror that in the UI
   * by hiding the button so the operator doesn't click into a 400.
   */
  isFullyCredited: boolean;
  onVoid: () => void;
  onRequestVoid: () => void;
  pendingSend?: boolean;
  pendingIssue?: boolean;
  pendingClone?: boolean;
  /** ADR-0030 permission gates — server enforces; these soften the UX. */
  canIssue?: boolean;
  canSend?: boolean;
  canVoid?: boolean;
  canRecordReceipt?: boolean;
  canRaiseCreditNote?: boolean;
}

/** Sticky page header — same shape as the composer's `.composer-head`. */
export function InvoiceDetailHeader({
  invoice,
  customer,
  isFounder,
  onEdit,
  onSendWhatsApp,
  onPrint,
  onDownloadPdf,
  onIssue,
  onDeleteDraft,
  onRecordReceipt,
  onClone,
  onRaiseCreditNote,
  isFullyCredited,
  onVoid,
  onRequestVoid,
  pendingSend,
  pendingIssue,
  pendingClone,
  canIssue = true,
  canSend = true,
  canVoid = isFounder,
  canRecordReceipt = true,
  canRaiseCreditNote = true,
}: InvoiceDetailHeaderProps) {
  const isDraft = invoice.status === "draft";
  const isVoided = invoice.status === "void";
  const isPaid = invoice.status === "paid";
  const isReversed = invoice.status === "reversed";
  const docNumber = invoice.docNumber ?? "Draft";
  const dueClass = dueEmphasisClass(invoice.status, invoice.dueOn);
  return (
    <header className="composer-head">
      <Link to="/sales/invoices" className="composer-head-back" aria-label="Back to invoices">
        <ArrowLeft size={16} strokeWidth={2.2} />
      </Link>
      <div className="composer-head-text">
        <h1>
          <span>Invoice</span>
          <span className="doc-num">{docNumber}</span>
          <span className={`status-pill ${statusVariant(invoice.status)}`}>
            {statusLabel(invoice.status)}
          </span>
        </h1>
        <p className="sub">
          <strong>{customer.name}</strong>
          <span className="sep" aria-hidden>
            ·
          </span>
          <span>
            Issued <span className="num">{formatDate(invoice.issuedOn)}</span>
          </span>
          <span className="sep" aria-hidden>
            ·
          </span>
          <span className={dueClass}>
            Due <span className="num">{formatDate(invoice.dueOn)}</span>
          </span>
          <span className="sep" aria-hidden>
            ·
          </span>
          <span className="num">{paiseToInrShort(invoice.totalPaise)}</span>
        </p>
      </div>
      <div className="composer-head-actions">
        {isDraft ? (
          <DraftActions
            onEdit={onEdit}
            onIssue={onIssue}
            onDelete={onDeleteDraft}
            canIssue={canIssue}
            {...(pendingIssue !== undefined ? { pending: pendingIssue } : {})}
          />
        ) : (
          <>
            {/* Group 1 · document actions — icon-only */}
            <button
              className="icon-action"
              type="button"
              onClick={onDownloadPdf}
              aria-label="Download PDF"
            >
              <Download size={15} />
            </button>
            <button
              className="icon-action"
              type="button"
              onClick={onPrint}
              aria-label="Print invoice"
            >
              <Printer size={15} />
            </button>

            <span className="action-sep" aria-hidden />

            {/* Group 2 · money & lifecycle — labelled ghost buttons */}
            {canRecordReceipt && !isVoided && !isPaid && !isReversed ? (
              <Button type="button" size="sm" onClick={onRecordReceipt}>
                <HandCoins size={14} />
                Record receipt
              </Button>
            ) : null}
            {/* Edit is only valid for drafts (the `isDraft` branch above
             *  routes there via DraftActions). Issued invoices are
             *  append-only per ADR-0007 — corrections happen via a
             *  credit note or a clone-and-reissue, not by editing the
             *  original. The Clone button below handles the "fix the
             *  paperwork" flow. */}
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={onClone}
              disabled={pendingClone}
            >
              <Copy size={14} />
              {pendingClone ? "Cloning…" : "Clone"}
            </Button>
            {canRaiseCreditNote && !isVoided && !isPaid && !isFullyCredited ? (
              <Button type="button" intent="ghost" size="sm" onClick={onRaiseCreditNote}>
                <FileMinus size={14} />
                Credit note
              </Button>
            ) : null}
            {canSend && !isVoided && !isPaid && !isReversed ? (
              <Button
                type="button"
                intent="ghost"
                size="sm"
                onClick={onSendWhatsApp}
                disabled={!customer.phone || pendingSend}
                title={!customer.phone ? "Customer has no phone on file" : undefined}
              >
                <Send size={14} />
                {pendingSend ? "Sending…" : "Send WhatsApp"}
              </Button>
            ) : null}

            {/* Group 3 · destructive — separated so the click pauses */}
            {!isVoided && !isDraft && !isPaid && !isReversed ? (
              <>
                <span className="action-sep" aria-hidden />
                {canVoid ? (
                  <Button type="button" intent="ghost" size="sm" onClick={onVoid}>
                    <Trash2 size={14} />
                    Void
                  </Button>
                ) : (
                  <Button type="button" intent="ghost" size="sm" onClick={onRequestVoid}>
                    <Send size={14} />
                    Request void
                  </Button>
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </header>
  );
}

/** Draft-only issue/edit/delete action group lifted from the old route. */
export function DraftActions({
  onEdit,
  onIssue,
  onDelete,
  canIssue = true,
  pending,
}: {
  onEdit: () => void;
  onIssue: (channel: "whatsapp" | "email" | "print-later" | "issue-only") => void;
  onDelete: () => void;
  canIssue?: boolean;
  pending?: boolean;
}) {
  return (
    <>
      <Button type="button" intent="ghost" size="sm" onClick={onDelete} disabled={pending}>
        <Trash2 size={14} />
        Delete
      </Button>
      <Button type="button" intent="ghost" size="sm" onClick={onEdit} disabled={pending}>
        <Pencil size={14} />
        Edit
      </Button>
      {canIssue && <div className="split">
        <button
          className="lead"
          type="button"
          onClick={() => onIssue("whatsapp")}
          disabled={pending}
        >
          {pending ? "Issuing…" : "Issue and send"}
        </button>
        <DropdownMenuRoot>
          <DropdownMenuTrigger asChild>
            <button
              className="chev"
              type="button"
              disabled={pending}
              aria-label="Pick send channel"
            >
              <ChevronDown size={12} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onIssue("whatsapp")}>
              <Send size={14} className="mr-2" />
              Issue and send via WhatsApp
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onIssue("email")}>
              Issue and send via Email
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onIssue("print-later")}>
              Issue and mark print later
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onIssue("issue-only")}>
              <FileText size={14} className="mr-2" />
              Issue only
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuRoot>
      </div>}
    </>
  );
}

/** Map invoice status to the page-scoped `.status-pill.{variant}`. */
export function statusVariant(status: InvoiceStatus): string {
  switch (status) {
    case "partially_paid":
      return "partial";
    case "paid":
      return "paid";
    case "overdue":
      return "overdue";
    case "void":
      return "void";
    case "reversed":
      // Reuse the void pill's muted/strike treatment — both represent
      // documents that no longer carry a receivable.
      return "void";
    case "draft":
      return "draft";
    case "issued":
      return "issued";
  }
}

/** Human label for invoice statuses in compact detail surfaces. */
export function statusLabel(status: InvoiceStatus): string {
  switch (status) {
    case "partially_paid":
      return "Partial";
    case "issued":
      return "Sent";
    case "void":
      return "Voided";
    case "reversed":
      return "Reversed";
    default:
      return status[0]!.toUpperCase() + status.slice(1);
  }
}

/** Pick the `.due-*` colour class based on status + due date. */
function dueEmphasisClass(status: InvoiceStatus, dueOn: string): string {
  if (status === "paid") return "due-ok";
  if (status === "overdue") return "due-late";
  const days = Math.ceil((new Date(dueOn).getTime() - Date.now()) / 86_400_000);
  if (days <= 7) return "due-soft";
  return "due-ok";
}
